import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, InputAdornment, Checkbox, Collapse, IconButton, LinearProgress,
} from '@mui/material';
import { ArrowBack, Save, Payment, AccountBalance, ExpandMore, ExpandLess, Receipt } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

export default function RegistrarCobro() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clienteIdParam = searchParams.get('clienteId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [mostrarRet, setMostrarRet] = useState({});

  const [cobro, setCobro] = useState({
    fecha: new Date().toISOString().split('T')[0],
    forma_pago: 'EFECTIVO',
    referencia: '',
    observaciones: '',
    cuenta_contable: '',
    cuenta_contable_id: '',
    banco_id: '',
  });

  useEffect(() => { cargarFacturas(); cargarCuentasContables(); cargarBancos(); }, []);

  useEffect(() => {
    if (!cuentasContables.length) return;
    if (cobro.forma_pago === 'EFECTIVO') {
      const c = cuentasContables.find(c => c.codigo === '1010101' || c.nombre?.toLowerCase().includes('caja'));
      if (c) setCobro(p => ({ ...p, cuenta_contable: c.codigo, cuenta_contable_id: c.id, banco_id: '' }));
    } else {
      const c = cuentasContables.find(c => c.codigo === '1010103' || c.nombre?.toLowerCase().includes('banco'));
      if (c) setCobro(p => ({ ...p, cuenta_contable: c.codigo, cuenta_contable_id: c.id }));
    }
  }, [cobro.forma_pago, cuentasContables]);

  const cargarCuentasContables = async () => {
    try {
      const r = await axios.get(`${API}/api/contabilidad/plan-cuentas`, cfg());
      const all = r.data?.data || r.data || [];
      const mov = all.filter(c => c.acepta_movimiento && (c.codigo?.startsWith('101010') || c.nombre?.toLowerCase().includes('caja') || c.nombre?.toLowerCase().includes('banco')));
      setCuentasContables(mov.length ? mov : all.filter(c => c.acepta_movimiento));
    } catch (_) {}
  };

  const cargarBancos = async () => {
    try { const r = await axios.get(`${API}/api/tesoreria/bancos`, cfg()); setBancos(r.data?.data || r.data || []); } catch (_) {}
  };

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const [facRes, cobRes, recRes] = await Promise.all([
        axios.get(`${API}/api/facturas`, cfg()),
        axios.get(`${API}/api/cuentas-cobrar/cobros`, cfg()).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/api/recibos`, cfg()).catch(() => ({ data: { data: [] } })),
      ]);
      const todas = facRes.data || [];
      const cobros = cobRes.data?.data || cobRes.data || [];
      const recibos = recRes.data?.data || [];

      const pendientes = todas.filter(f => f.estadoSRI === 'AUTORIZADO').map(f => {
        const total = parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0);
        const cobrado = cobros.filter(c => c.factura_id === (f.id||f.claveAcceso) || c.factura_clave === f.claveAcceso)
          .reduce((s, c) => s + parseFloat(c.monto||0) + parseFloat(c.ret_fuente_monto||0) + parseFloat(c.ret_iva_monto||0), 0);
        return { ...f, total, cobrado, saldo: Math.max(0, total - cobrado) };
      }).filter(f => f.saldo > 0.01);

      const CRED = ['credito_plazo','credito','Crédito','CREDITO'];
      const recPend = recibos.filter(r => r.estado !== 'ANULADO' && CRED.includes(r.forma_pago)).map(r => {
        const total = parseFloat(r.totales?.total || 0);
        const cobrado = cobros.filter(c => c.factura_id === r.id).reduce((s, c) => s + parseFloat(c.monto||0), 0);
        return { id: r.id, tipo_documento: 'RECIBO', secuencial: r.numero, cliente: r.cliente, fechaEmision: r.fecha ? new Date(r.fecha).toLocaleDateString('es-EC') : '', totales: { valorPagar: total }, total, cobrado, saldo: Math.max(0, total - cobrado) };
      }).filter(r => r.saldo > 0.01);

      const lista = [...pendientes, ...recPend];
      setFacturasPendientes(lista);

      if (clienteIdParam) {
        const f = lista.find(f => f.cliente?.ruc === clienteIdParam || f.cliente?.id === clienteIdParam);
        if (f) toggleSeleccion(f, lista);
      }
    } catch (_) { setSnackbar({ open: true, message: 'Error al cargar facturas', severity: 'error' }); }
    finally { setLoading(false); }
  };

  const toggleSeleccion = (f, base = facturasPendientes) => {
    const fid = f.id || f.claveAcceso;
    const sel = facturasSeleccionadas.find(fs => fs.factura_id === fid);
    if (sel) {
      setFacturasSeleccionadas(p => p.filter(fs => fs.factura_id !== fid));
    } else {
      setFacturasSeleccionadas(p => [...p, { factura_id: fid, factura: f, monto: f.saldo.toFixed(2), ret_fuente_monto: '', ret_iva_monto: '' }]);
    }
  };

  const actualizarFS = (fid, campo, valor) => setFacturasSeleccionadas(p => p.map(fs => fs.factura_id === fid ? { ...fs, [campo]: valor } : fs));

  const totalEfectivo  = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.monto || 0), 0);
  const totalRetFuente = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.ret_fuente_monto || 0), 0);
  const totalRetIVA    = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.ret_iva_monto || 0), 0);
  const totalACancelar = totalEfectivo + totalRetFuente + totalRetIVA;
  const mostrarBancos  = ['TRANSFERENCIA','CHEQUE','DEPOSITO','TARJETA'].includes(cobro.forma_pago);

  const guardar = async () => {
    if (!facturasSeleccionadas.length) return setSnackbar({ open: true, message: 'Seleccione al menos una factura', severity: 'error' });
    if (!cobro.cuenta_contable) return setSnackbar({ open: true, message: 'Seleccione una cuenta contable', severity: 'error' });
    // Validar banco obligatorio cuando la forma de pago no es efectivo
    if (mostrarBancos && !cobro.banco_id) {
      if (bancos.length === 0) return setSnackbar({ open: true, message: 'No tiene cuentas bancarias configuradas. Configure una en Tesorería → Bancos antes de registrar este cobro.', severity: 'error' });
      return setSnackbar({ open: true, message: 'Debe seleccionar el banco o cuenta bancaria donde se recibe el pago', severity: 'error' });
    }
    for (const fs of facturasSeleccionadas) {
      const tot = parseFloat(fs.monto||0) + parseFloat(fs.ret_fuente_monto||0) + parseFloat(fs.ret_iva_monto||0);
      if (tot <= 0) return setSnackbar({ open: true, message: 'El monto debe ser mayor a 0', severity: 'error' });
      if (tot > fs.factura.saldo + 0.01) return setSnackbar({ open: true, message: `Monto excede el saldo de factura ${String(fs.factura.secuencial||'').padStart(9,'0')}`, severity: 'error' });
    }
    setSaving(true);
    try {
      const banco = bancos.find(b => b.id === cobro.banco_id);
      if (facturasSeleccionadas.length === 1) {
        const fs = facturasSeleccionadas[0];
        await axios.post(`${API}/api/cuentas-cobrar/cobros`, {
          factura_id: fs.factura_id, cliente_id: fs.factura.cliente?.id || fs.factura.cliente?.ruc,
          cliente_nombre: fs.factura.cliente?.nombre || fs.factura.cliente?.razonSocial,
          factura_secuencial: String(fs.factura.secuencial||'').padStart(9,'0'),
          total_factura: fs.factura.total, monto: parseFloat(fs.monto),
          ret_fuente_monto: parseFloat(fs.ret_fuente_monto||0), ret_iva_monto: parseFloat(fs.ret_iva_monto||0),
          ...cobro, cuenta_contable: cobro.cuenta_contable_id || cobro.cuenta_contable, banco_nombre: banco?.nombre || '',
        }, cfg());
      } else {
        await axios.post(`${API}/api/cuentas-cobrar/cobros/multiple`, {
          cliente_id: facturasSeleccionadas[0]?.factura?.cliente?.id,
          cliente_nombre: facturasSeleccionadas[0]?.factura?.cliente?.nombre,
          ...cobro, cuenta_contable: cobro.cuenta_contable_id || cobro.cuenta_contable,
          facturas: facturasSeleccionadas.map(fs => ({ factura_id: fs.factura_id, monto: parseFloat(fs.monto), ret_fuente_monto: parseFloat(fs.ret_fuente_monto||0), ret_iva_monto: parseFloat(fs.ret_iva_monto||0), total_factura: fs.factura.total })),
        }, cfg());
      }
      setSnackbar({ open: true, message: 'Cobro registrado y asiento contable generado.', severity: 'success' });
      setFacturasSeleccionadas([]);
      setCobro(p => ({ ...p, referencia: '', observaciones: '' }));
      cargarFacturas();
    } catch (e) { setSnackbar({ open: true, message: e.response?.data?.message || e.response?.data?.error || 'Error al registrar cobro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', boxSizing: 'border-box' }}>

      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/cuentas-cobrar')} variant="outlined" size="small">Volver</Button>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Payment sx={{ color: '#1565c0' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Registrar Cobro</Typography>
          <Typography variant="body2" color="text.secondary">Selecciona facturas y aplica el cobro con asiento contable automático</Typography>
        </Box>
      </Box>

      {facturasPendientes.length === 0 ? (
        <Alert severity="info">No hay facturas con saldo pendiente de cobro.</Alert>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

          {/* ── COLUMNA IZQUIERDA ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Tabla de facturas */}
            <Paper sx={{ borderRadius: 2 }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#1565c0', color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ fontSize: 18 }} />
                <Box>
                  <Typography fontWeight={700} fontSize={14}>Facturas con Saldo Pendiente</Typography>
                  <Typography sx={{ fontSize: 11, opacity: .85 }}>Selecciona una o más. Expande para ingresar retenciones del cliente.</Typography>
                </Box>
                <Chip label={facturasPendientes.length} size="small" sx={{ bgcolor: '#fff', color: '#1565c0', fontWeight: 700, ml: 'auto' }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontSize: 11, fontWeight: 700, py: 0.75 } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Factura</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell sx={{ minWidth: 90 }}>Cobrado</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell align="right">Monto Cobrar</TableCell>
                      <TableCell align="center">Retenciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facturasPendientes.map((f, i) => {
                      const fid = f.id || f.claveAcceso;
                      const sel = facturasSeleccionadas.find(fs => fs.factura_id === fid);
                      const verRet = mostrarRet[fid];
                      const progreso = f.total > 0 ? Math.round((f.cobrado / f.total) * 100) : 0;
                      return (
                        <>
                          <TableRow key={fid} hover selected={!!sel}
                            sx={{ bgcolor: sel ? '#e3f2fd' : i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer', '& td': { fontSize: 12, py: 0.8 } }}
                            onClick={() => toggleSeleccion(f)}>
                            <TableCell padding="checkbox"><Checkbox checked={!!sel} size="small" onChange={() => {}} /></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {f.tipo_documento === 'RECIBO'
                                ? <Chip label={`RCV ${f.secuencial}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                                : String(f.secuencial || '').padStart(9, '0')}
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{f.cliente?.nombre || f.cliente?.razonSocial || '—'}</Typography>
                              <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{f.cliente?.ruc || ''}</Typography>
                            </TableCell>
                            <TableCell>{f.fechaEmision}</TableCell>
                            <TableCell align="right">{fmt(f.total)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LinearProgress variant="determinate" value={progreso}
                                  sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: progreso > 0 ? '#4caf50' : '#e0e0e0' } }} />
                                <Typography sx={{ fontSize: 10, minWidth: 24 }}>{progreso}%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(f.saldo)}</TableCell>
                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                              {sel && (
                                <TextField type="number" size="small" value={sel.monto}
                                  onChange={e => actualizarFS(fid, 'monto', e.target.value)}
                                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: 12 } }}>$</InputAdornment> }}
                                  inputProps={{ min: 0, max: f.saldo, step: 0.01 }} sx={{ width: 110 }} />
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={e => e.stopPropagation()}>
                              {sel && (
                                <IconButton size="small" onClick={() => setMostrarRet(p => ({ ...p, [fid]: !verRet }))}>
                                  {verRet ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Fila retenciones */}
                          {sel && verRet && (
                            <TableRow key={`ret-${fid}`} sx={{ bgcolor: '#fffde7' }}>
                              <TableCell colSpan={9} sx={{ py: 1.5, px: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 200 }}>
                                    💡 Retenciones que realizó el cliente sobre esta factura:
                                  </Typography>
                                  <TextField size="small" label="Ret. Renta (IR)" type="number"
                                    value={sel.ret_fuente_monto}
                                    onChange={e => actualizarFS(fid, 'ret_fuente_monto', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    inputProps={{ min: 0, step: 0.01 }} sx={{ width: 140 }}
                                    helperText="Crédito tributario IR" />
                                  <TextField size="small" label="Ret. IVA" type="number"
                                    value={sel.ret_iva_monto}
                                    onChange={e => actualizarFS(fid, 'ret_iva_monto', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    inputProps={{ min: 0, step: 0.01 }} sx={{ width: 140 }}
                                    helperText="Crédito tributario IVA" />
                                  {(parseFloat(sel.ret_fuente_monto||0) + parseFloat(sel.ret_iva_monto||0)) > 0 && (
                                    <Chip size="small" color="info"
                                      label={`Total cancelado: ${fmt(parseFloat(sel.monto||0)+parseFloat(sel.ret_fuente_monto||0)+parseFloat(sel.ret_iva_monto||0))}`} />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Formulario del cobro — solo visible si hay seleccionadas */}
            {facturasSeleccionadas.length > 0 && (
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 2 }}>💳 Datos del Cobro</Typography>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <TextField size="small" required type="date" label="Fecha" value={cobro.fecha}
                    onChange={e => setCobro({ ...cobro, fecha: e.target.value })} InputLabelProps={{ shrink: true }} />
                  <FormControl fullWidth required size="small">
                    <InputLabel>Forma de Pago</InputLabel>
                    <Select value={cobro.forma_pago} label="Forma de Pago"
                      onChange={e => setCobro({ ...cobro, forma_pago: e.target.value })}>
                      <MenuItem value="EFECTIVO">💵 Efectivo</MenuItem>
                      <MenuItem value="TRANSFERENCIA">🏦 Transferencia</MenuItem>
                      <MenuItem value="CHEQUE">📄 Cheque</MenuItem>
                      <MenuItem value="DEPOSITO">🏧 Depósito</MenuItem>
                      <MenuItem value="TARJETA">💳 Tarjeta</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Referencia / No. Comprobante" value={cobro.referencia}
                    onChange={e => setCobro({ ...cobro, referencia: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: mostrarBancos ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 12 }}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Cuenta Contable (Destino del dinero)</InputLabel>
                    <Select value={cobro.cuenta_contable} label="Cuenta Contable (Destino del dinero)"
                      onChange={e => { const c = cuentasContables.find(x => x.codigo === e.target.value); setCobro({ ...cobro, cuenta_contable: e.target.value, cuenta_contable_id: c?.id || e.target.value }); }}>
                      {cuentasContables.map(c => <MenuItem key={c.id || c.codigo} value={c.codigo}>{c.codigo} - {c.nombre}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {mostrarBancos && (
                    <FormControl fullWidth size="small" error={mostrarBancos && !cobro.banco_id}>
                      <InputLabel>Banco *</InputLabel>
                      <Select value={cobro.banco_id} label="Banco *"
                        onChange={e => setCobro({ ...cobro, banco_id: e.target.value })}>
                        <MenuItem value="">— Seleccione banco —</MenuItem>
                        {bancos.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre_banco || b.nombre}{b.numero_cuenta ? ` (****${String(b.numero_cuenta).slice(-4)})` : ''}</MenuItem>)}
                      </Select>
                      {mostrarBancos && !cobro.banco_id && (
                        bancos.length === 0
                          ? <Box sx={{ mt: 1 }}>
                              <Alert severity="warning" sx={{ fontSize: 11, py: 0.5 }}
                                action={<Button size="small" onClick={() => navigate('/tesoreria/bancos')} sx={{ fontSize: 10, textTransform: 'none' }}>Configurar →</Button>}>
                                Sin cuentas bancarias. Configure una en Tesorería primero.
                              </Alert>
                            </Box>
                          : <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5, ml: 1.5 }}>
                              Seleccione el banco donde ingresa el dinero
                            </Typography>
                      )}
                    </FormControl>
                  )}
                </div>
                <TextField fullWidth size="small" multiline rows={2} label="Observaciones"
                  value={cobro.observaciones} onChange={e => setCobro({ ...cobro, observaciones: e.target.value })} />

                {(totalRetFuente + totalRetIVA) > 0 && (
                  <Alert severity="info" sx={{ mt: 1.5, fontSize: 11 }} icon={<AccountBalance fontSize="small" />}>
                    <strong>Asiento:</strong> DEBE Caja/Banco {fmt(totalEfectivo)}
                    {totalRetFuente > 0 && ` + Ret.IR ${fmt(totalRetFuente)}`}
                    {totalRetIVA > 0 && ` + Ret.IVA ${fmt(totalRetIVA)}`}
                    {` → HABER CxC ${fmt(totalACancelar)}`}
                  </Alert>
                )}
              </Paper>
            )}
          </Box>

          {/* ── COLUMNA DERECHA: RESUMEN ── */}
          <Box sx={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2.5, borderRadius: 2, border: '2px solid #1565c0' }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccountBalance fontSize="small" sx={{ color: '#1565c0' }} /> Resumen del Cobro
              </Typography>

              {facturasSeleccionadas.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography sx={{ fontSize: 36 }}>☑️</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Selecciona facturas de la lista para cobrar</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Typography variant="body2" color="text.secondary">Facturas seleccionadas</Typography>
                      <Chip label={facturasSeleccionadas.length} size="small" color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Typography variant="body2" color="text.secondary">Efectivo / transferencia</Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">{fmt(totalEfectivo)}</Typography>
                    </Box>
                    {totalRetFuente > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography variant="body2" color="text.secondary">Retención IR</Typography>
                        <Typography variant="body2" fontWeight={700} color="secondary.main">{fmt(totalRetFuente)}</Typography>
                      </Box>
                    )}
                    {totalRetIVA > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography variant="body2" color="text.secondary">Retención IVA</Typography>
                        <Typography variant="body2" fontWeight={700} color="secondary.main">{fmt(totalRetIVA)}</Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">TOTAL A CANCELAR</Typography>
                    <Typography variant="h4" fontWeight={700} color="#1565c0" sx={{ lineHeight: 1.2 }}>{fmt(totalACancelar)}</Typography>
                  </Box>

                  <Alert severity="success" sx={{ fontSize: 11, py: 0.5 }}>
                    Se generará asiento contable automático al registrar el cobro.
                  </Alert>
                </>
              )}

              <Button fullWidth variant="contained" size="large" onClick={guardar}
                disabled={saving || facturasSeleccionadas.length === 0}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                sx={{ mt: 2, bgcolor: '#1565c0', fontWeight: 700, py: 1.25 }}>
                {saving ? 'Registrando...' : 'Registrar Cobro'}
              </Button>
              <Button fullWidth variant="outlined" onClick={() => navigate('/cuentas-cobrar')}
                disabled={saving} sx={{ mt: 1 }}>
                Cancelar
              </Button>
            </Paper>
          </Box>
        </div>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
